import { useState, useRef, useCallback, useEffect } from "react";
import { getCurrentLanguage } from "../i18n";

// Description de l'outil `hangUp`, dans la langue de l'utilisateur (cf. usage plus bas).
const HANG_UP_DESCRIPTION: Record<"fr" | "en", string> = {
  fr: "Met fin à l'appel téléphonique. Déclencheur STRICT : appelle cette fonction UNIQUEMENT si TON INTERLOCUTEUR (la personne qui t'appelle) a lui-même explicitement pris congé et mis fin à l'échange (il dit « au revoir », « bonne journée », « je vous laisse »… et clôt la conversation). C'est un FAIT OBSERVABLE (l'autre prend congé), pas ton appréciation que « la conversation est terminée ». NE RACCROCHE JAMAIS de ta propre initiative. Faux déclencheurs — NE PAS raccrocher : un rendez-vous vient d'être proposé ou fixé ; l'objectif de l'appel te semble atteint ; la découverte/l'échange est encore en cours ; c'est TOI qui viens de dire au revoir ou de conclure (auto-congé). En cas de doute, NE RACCROCHE PAS : laisse toujours ton interlocuteur décider quand l'appel se termine.",
  en: "Ends the phone call. STRICT trigger: call this function ONLY if YOUR INTERLOCUTOR (the person calling you) has themselves explicitly said goodbye and closed the exchange (they say \"goodbye\", \"have a good day\", \"I'll let you go\"… and end the conversation). This is an OBSERVABLE FACT (the other person takes their leave), not your own judgement that \"the conversation is over\". NEVER hang up on your own initiative. False triggers — DO NOT hang up: a meeting has just been proposed or booked; the goal of the call seems reached to you; the discovery/exchange is still ongoing; YOU are the one who just said goodbye or wrapped up (self-closing). When in doubt, DO NOT HANG UP: always let your interlocutor decide when the call ends.",
};

/**
 * Extrait le `call_id` OpenAI Realtime (format `rtc_…`) depuis l'en-tête HTTP
 * `Location` renvoyé par `POST /v1/realtime/calls`. Le header a la forme
 * `/v1/realtime/calls/rtc_abc123` → on garde le dernier segment.
 *
 * Pur (testable sans DOM). Renvoie `null` si l'en-tête est absent/vide ou si
 * aucun segment exploitable n'en ressort (best-effort : le call_id reste optionnel).
 */
export function extractCallIdFromLocation(location: string | null | undefined): string | null {
  if (!location) return null;
  // On ignore une éventuelle query string / fragment puis on prend le dernier
  // segment de chemin non vide (tolère un slash final).
  const path = (location.split(/[?#]/)[0] ?? "").replace(/\/+$/, "");
  const callId = path.split("/").pop();
  return callId && callId.length > 0 ? callId : null;
}

export interface RealtimeMessage {
  id: string;
  role: "user" | "agent";
  content: string;
  timestamp: string;
  /**
   * `item_id` de l'item OpenAI Realtime dont ce message est la transcription.
   *
   * C'est le SEUL identifiant partagé entre ce que le navigateur enregistre et ce
   * que le contrôleur sideband observe — les deux sont attachés au même appel, donc
   * à la même conversation. C'est lui qui permet de rattacher une consigne ou une
   * note de score au tour qu'elle vise, sans dépendre d'aucune horloge.
   */
  itemId?: string;
}

export interface AgentConfig {
  client_secret: { value: string };
  model?: string;
  voice?: string;
  name?: string;
  instructions?: string;
}

interface UseRealtimeSessionOptions {
  onMessage?: (message: RealtimeMessage) => void;
  onAgentSpeakingChange?: (speaking: boolean) => void;
  onError?: (error: Error) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onMuteChange?: (muted: boolean) => void;
  onUserSpeechStarted?: () => void;
  /**
   * Flux audio temps réel de l'agent (sa voix), exposé dès réception via
   * `pc.ontrack`. Permet à l'UI de brancher un visualiseur waveform
   * (AnalyserNode WebAudio). `null` est émis à la fermeture de session.
   */
  onAgentAudioStream?: (stream: MediaStream | null) => void;
  /**
   * Appelé une fois que la session OpenAI Realtime est ouverte et que le
   * `call_id` (format `rtc_…`) a pu être extrait de l'en-tête `Location` de
   * `POST /v1/realtime/calls`. Sert au relais sideband (cf. POST /api/sideband).
   * Best-effort : non appelé si l'en-tête `Location` est absent/illisible.
   */
  onCallId?: (callId: string) => void;
  /**
   * Désactive l'outil `hangUp` (et l'auto-disconnect associé). À activer pour les
   * sessions où l'IA ne doit JAMAIS raccrocher d'elle-même (ex. coach de « S'exercer »,
   * format rounds) : la session ne se termine que sur action explicite de l'utilisateur.
   * L'outil `hangUp` est taillé pour une simulation téléphonique (le prospect prend congé)
   * et coupe prématurément un coaching. Défaut `false` (comportement simulateur inchangé).
   */
  disableHangUp?: boolean;
  /**
   * DIAGNOSTIC (observabilité) — appelé à chaque terminaison de session (et sur
   * `ice_disconnected`) avec la RAISON du drop + le contexte WebRTC. Permet de
   * diagnostiquer les « simulations qui coupent en pleine conversation » (la raison
   * du drop est côté navigateur et n'était captée nulle part). Best-effort.
   */
  onEndDiagnostic?: (diag: RealtimeEndDiagnostic) => void;
}

export interface RealtimeEndDiagnostic {
  reason: "user_hangup" | "agent_hangup_tool" | "ice_failed" | "ice_disconnected" | "dc_close" | "server_error" | "connect_error" | "goodbye_timeout" | "calling_inactivity";
  iceState?: string;
  connState?: string;
  iceGathering?: string;
  candTypeLocal?: string;   // host | srflx | prflx | relay
  candTypeRemote?: string;
  protocol?: string;        // udp | tcp
  serverError?: string | null;
  elapsedMs?: number;
  model?: string;
  networkType?: string;
}

/**
 * Problème de micro détecté APRÈS un getUserMedia réussi (donc sans exception).
 * - "denied"    : permission refusée (navigateur ou OS)
 * - "no-device" : aucun micro disponible / piste terminée
 * - "silent"    : la piste existe mais le système ne fournit aucun son
 *                 (`track.muted === true`). Cas typique après une mise à jour
 *                 macOS (Tahoe) qui réinitialise l'autorisation micro de Chrome :
 *                 l'onglet "croit" enregistrer mais l'OS n'envoie que du silence.
 */
export type MicIssue = "denied" | "no-device" | "silent" | null;

interface RealtimeSessionState {
  isConnected: boolean;
  isAgentSpeaking: boolean;
  error: string | null;
  micIssue: MicIssue;
  /**
   * Transcription en cours de la réplique de l'agent (mot à mot, mise à jour
   * via les events `response.audio_transcript.delta`). Sert au sous-titre live
   * pendant l'appel. Vide quand l'agent ne parle pas encore / session fermée.
   */
  liveTranscript: string;
}

/**
 * Direct WebRTC connection to OpenAI Realtime API.
 * No dependency on @openai/agents SDK — uses the ephemeral key from N8N directly.
 */
export function useRealtimeSession(options: UseRealtimeSessionOptions = {}) {
  const [state, setState] = useState<RealtimeSessionState>({
    isConnected: false,
    isAgentSpeaking: false,
    error: null,
    micIssue: null,
    liveTranscript: "",
  });

  // Store latest options in a ref so event handlers always use current callbacks
  // This fixes stale closure issues — dc.onmessage is set once at connect time
  // but optionsRef.current always points to the latest callbacks
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; }, [options]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  // Garde anti-double-connexion : passe à true dès l'entrée dans connect() et
  // jusqu'à ce que `pcRef` soit posé (ou erreur). Évite d'ouvrir 2 peer connections
  // si connect() est appelé 2× (StrictMode / re-clic) → sinon coach dédoublé + micro
  // non entendu sur la 1ʳᵉ connexion orpheline.
  const connectingRef = useRef(false);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const messageIdRef = useRef(0);
  // Track partial transcripts per item
  const transcriptsRef = useRef<Map<string, { role: "user" | "agent"; parts: string[] }>>(new Map());
  // Cumul des tokens consommés via les events response.done — cf. spec/cost-tracking.md
  // Lu à la fin de session par TrainingPage pour appeler trainingApi.logSessionCost.
  const usageAccRef = useRef({
    inputTokens: 0,
    outputTokens: 0,
    cachedTokens: 0,
    // Split audio/texte (détails OpenAI) — analytique, cumulé sur les response.done.
    audioInputTokens: 0,
    textInputTokens: 0,
    audioOutputTokens: 0,
    textOutputTokens: 0,
    model: "" as string,
  });
  // DIAGNOSTIC — capter POURQUOI une session se termine (cf. onEndDiagnostic).
  const connectStartRef = useRef<number>(0);           // Date.now() au début de connect()
  const lastServerErrorRef = useRef<string | null>(null); // dernier event "error" OpenAI
  const endDiagSentRef = useRef(false);                // garde : 1 seule capture de FIN par session
  const endReasonRef = useRef<RealtimeEndDiagnostic["reason"] | null>(null); // raison la plus spécifique connue
  const iceDisconnectLoggedRef = useRef(false);        // capte 1× l'event ice_disconnected (indicateur avancé)

  // Type de la paire de candidats ICE active (host/srflx/prflx/relay) — signal CLÉ :
  // un drop sur chemin direct (host/srflx) suggère qu'un serveur TURN aiderait.
  const getActiveCandidatePair = useCallback(async (pc: RTCPeerConnection) => {
    try {
      const stats = await pc.getStats();
      let pair: any = null;
      stats.forEach((r: any) => {
        if (r.type === "candidate-pair" && r.state === "succeeded" && (r.nominated || r.selected)) pair = r;
      });
      if (!pair) stats.forEach((r: any) => { if (r.type === "candidate-pair" && r.state === "succeeded") pair = r; });
      if (!pair) return {};
      const local: any = stats.get(pair.localCandidateId);
      const remote: any = stats.get(pair.remoteCandidateId);
      return { candTypeLocal: local?.candidateType, candTypeRemote: remote?.candidateType, protocol: local?.protocol };
    } catch {
      return {};
    }
  }, []);

  // Capture la raison + le contexte WebRTC à une fin de session. `isEnd=true` =
  // terminaison (garde 1×/session, prend la raison la plus spécifique) ; `isEnd=false`
  // = event intermédiaire (ex. ice_disconnected qui ne coupe pas forcément).
  const captureEndDiagnostic = useCallback(async (reason: RealtimeEndDiagnostic["reason"], isEnd = true) => {
    if (isEnd) {
      if (endDiagSentRef.current) return;
      endDiagSentRef.current = true;
    }
    const finalReason = isEnd && endReasonRef.current ? endReasonRef.current : reason;
    const pc = pcRef.current;
    let cand: { candTypeLocal?: string; candTypeRemote?: string; protocol?: string } = {};
    if (pc) cand = await getActiveCandidatePair(pc);
    const conn = (navigator as any).connection;
    const diag: RealtimeEndDiagnostic = {
      reason: finalReason,
      iceState: pc?.iceConnectionState,
      connState: pc?.connectionState,
      iceGathering: pc?.iceGatheringState,
      candTypeLocal: cand.candTypeLocal,
      candTypeRemote: cand.candTypeRemote,
      protocol: cand.protocol,
      serverError: lastServerErrorRef.current,
      elapsedMs: connectStartRef.current ? Date.now() - connectStartRef.current : undefined,
      model: usageAccRef.current.model || undefined,
      networkType: conn?.effectiveType || conn?.type,
    };
    try { optionsRef.current.onEndDiagnostic?.(diag); } catch { /* best-effort */ }
  }, [getActiveCandidatePair]);
  // Timer ref to cancel previous unmute timers when a new response starts
  const unmuteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track user's manual mute intent — prevents auto-unmute from overriding user choice
  const userMutedRef = useRef(false);
  // Track agent speaking state via ref (no stale closure issues, used by waitForSilence)
  const isAgentSpeakingRef = useRef(false);
  // Sous-titre live : texte courant accumulé + flush throttlé via rAF pour éviter
  // un setState à chaque delta (10-30/s) qui re-rendrait toute la TrainingPage.
  const liveTextRef = useRef("");
  const liveRafRef = useRef<number | null>(null);
  const scheduleLiveFlush = useCallback(() => {
    if (liveRafRef.current != null) return;
    liveRafRef.current = requestAnimationFrame(() => {
      liveRafRef.current = null;
      setState((prev) =>
        prev.liveTranscript === liveTextRef.current
          ? prev
          : { ...prev, liveTranscript: liveTextRef.current }
      );
    });
  }, []);
  /**
   * Wait for the agent to stop speaking, then wait bufferMs for the audio
   * buffer to finish playing. Calls `callback` when done.
   * Safety timeout at maxWaitMs to prevent infinite waits.
   */
  const waitForAgentSilenceThen = (callback: () => void, maxWaitMs = 15000, bufferMs = 2500) => {
    // Not connected — call immediately
    if (!pcRef.current || !dcRef.current) {
      callback();
      return;
    }

    // Track how long the agent has been silent
    let silentSince: number | null = isAgentSpeakingRef.current ? null : Date.now();

    const check = setInterval(() => {
      if (!isAgentSpeakingRef.current) {
        if (silentSince === null) silentSince = Date.now();
        if (Date.now() - silentSince >= bufferMs) {
          clearInterval(check);
          clearTimeout(safety);
          callback();
        }
      } else {
        // Agent started speaking again — reset silence timer
        silentSince = null;
      }
    }, 200);

    const safety = setTimeout(() => {
      clearInterval(check);
      console.warn(`[realtime] waitForSilence safety timeout (${maxWaitMs}ms)`);
      callback();
    }, maxWaitMs);
  };

  // Cleanup network resources only (data channel, peer connection, mic)
  // Does NOT stop audio playback — agent's last phrase keeps playing
  const cleanupNetwork = useCallback(() => {
    // Cancel any pending unmute timer
    if (unmuteTimerRef.current) {
      clearTimeout(unmuteTimerRef.current);
      unmuteTimerRef.current = null;
    }
    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    transcriptsRef.current.clear();
    isAgentSpeakingRef.current = false;
    // Reset du sous-titre live + annulation du flush en attente
    if (liveRafRef.current != null) {
      cancelAnimationFrame(liveRafRef.current);
      liveRafRef.current = null;
    }
    liveTextRef.current = "";
    // Le flux audio de l'agent n'est plus disponible — notifie le visualiseur
    optionsRef.current.onAgentAudioStream?.(null);
    setState({
      isConnected: false,
      isAgentSpeaking: false,
      error: null,
      micIssue: null,
      liveTranscript: "",
    });
  }, []);

  // Full cleanup including audio — called only on explicit disconnect or error
  const cleanupAll = useCallback(() => {
    cleanupNetwork();
    // Let the audio buffer finish playing, then clean up after a delay
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
        audioRef.current = null;
      }
    }, 3000);
  }, [cleanupNetwork]);

  const disconnect = useCallback(() => {
    // DIAGNOSTIC : capter la fin AVANT le teardown (pc encore lisible). Le hangUp
    // pose endReasonRef="agent_hangup_tool" avant d'appeler disconnect ; sinon =
    // raccrochage volontaire de l'utilisateur.
    void captureEndDiagnostic(endReasonRef.current ?? "user_hangup");
    cleanupAll();
    optionsRef.current.onDisconnected?.();
  }, [cleanupAll, captureEndDiagnostic]);

  const handleServerEvent = useCallback((msg: any) => {
    // Migration GA OpenAI Realtime (mai 2026) — les events audio + transcript
    // ont été renommés avec le préfixe `output_`. On normalise vers les anciens
    // noms pour que le reste du handler continue de fonctionner sans modif.
    //   response.output_audio.delta            → response.audio.delta
    //   response.output_audio.done             → response.audio.done
    //   response.output_audio_transcript.delta → response.audio_transcript.delta
    //   response.output_audio_transcript.done  → response.audio_transcript.done
    // Sans cette normalisation, le frontend n'attrapait plus les transcripts
    // de l'agent → BizCoach envoyait une transcription vide à N8N feedback
    // → N8N retournait `non_applicable` → scores tous à 0 (incident 22/05).
    const rawType = msg.type as string;
    const type = rawType
      ?.replace(/^response\.output_audio_transcript\./, "response.audio_transcript.")
      ?.replace(/^response\.output_audio\./, "response.audio.");
    const opts = optionsRef.current;

    // Agent speaking state — mute mic to prevent echo feedback
    if (type === "response.audio.delta") {
      isAgentSpeakingRef.current = true;
      // Cancel any pending unmute timer from a previous response
      // This prevents the timer from firing mid-speech and unmuting the mic
      if (unmuteTimerRef.current) {
        clearTimeout(unmuteTimerRef.current);
        unmuteTimerRef.current = null;
      }
      // Mute mic while agent speaks to prevent echo loop
      if (streamRef.current) {
        streamRef.current.getAudioTracks().forEach((t) => { t.enabled = false; });
      }
      setState((prev) => {
        if (!prev.isAgentSpeaking) {
          opts.onAgentSpeakingChange?.(true);
          return { ...prev, isAgentSpeaking: true };
        }
        return prev;
      });
    }

    // response.audio.done = audio DATA sent (but still playing in buffer)
    // response.done = entire response complete — safe to unmute
    if (type === "response.audio.done") {
      isAgentSpeakingRef.current = false;
      // Audio data finished sending — do NOT unmute yet, buffer still playing
      setState((prev) => {
        if (prev.isAgentSpeaking) {
          opts.onAgentSpeakingChange?.(false);
          return { ...prev, isAgentSpeaking: false };
        }
        return prev;
      });
    }

    if (type === "response.done") {
      isAgentSpeakingRef.current = false;
      // Capture des tokens pour cost tracking — cf. spec/cost-tracking.md §4.1
      // OpenAI envoie usage.{input,output}_tokens (totaux) + détails par type.
      const usage = msg.response?.usage;
      if (usage) {
        usageAccRef.current.inputTokens += usage.input_tokens ?? 0;
        usageAccRef.current.outputTokens += usage.output_tokens ?? 0;
        usageAccRef.current.cachedTokens += usage.input_token_details?.cached_tokens ?? 0;
        // Split audio/texte (input & output) — cf. spec/chantier-realtime-token-split.md
        usageAccRef.current.audioInputTokens += usage.input_token_details?.audio_tokens ?? 0;
        usageAccRef.current.textInputTokens += usage.input_token_details?.text_tokens ?? 0;
        usageAccRef.current.audioOutputTokens += usage.output_token_details?.audio_tokens ?? 0;
        usageAccRef.current.textOutputTokens += usage.output_token_details?.text_tokens ?? 0;
      }
      setState((prev) => {
        if (prev.isAgentSpeaking) {
          opts.onAgentSpeakingChange?.(false);
          return { ...prev, isAgentSpeaking: false };
        }
        return prev;
      });
      // Re-enable microphone after agent finishes speaking
      // Only if user hasn't manually muted (respects red button state)
      if (unmuteTimerRef.current) {
        clearTimeout(unmuteTimerRef.current);
      }
      unmuteTimerRef.current = setTimeout(() => {
        unmuteTimerRef.current = null;
        if (!userMutedRef.current && streamRef.current) {
          streamRef.current.getAudioTracks().forEach((t) => { t.enabled = true; });
          opts.onMuteChange?.(false);
        }
      }, 2500);
    }

    // Transcript accumulation — agent output
    if (type === "response.audio_transcript.delta") {
      const itemId = msg.item_id;
      if (itemId) {
        const entry = transcriptsRef.current.get(itemId) || { role: "agent" as const, parts: [] };
        entry.parts.push(msg.delta || "");
        transcriptsRef.current.set(itemId, entry);
        // Sous-titre live : l'entrée est supprimée au `done`, donc une nouvelle
        // réplique repart d'un buffer vide → remplacement naturel à l'écran.
        liveTextRef.current = entry.parts.join("");
        scheduleLiveFlush();
      }
    }

    if (type === "response.audio_transcript.done") {
      const itemId = msg.item_id;
      const transcript = msg.transcript || "";
      if (itemId && transcript) {
        // Emit final agent message
        transcriptsRef.current.delete(itemId);
        opts.onMessage?.({
          id: `msg-${++messageIdRef.current}`,
          role: "agent",
          content: transcript,
          timestamp: new Date().toISOString(),
          itemId,
        });
      }
    }

    // User speech transcript (from input_audio_transcription)
    if (type === "conversation.item.input_audio_transcription.completed") {
      const transcript = msg.transcript?.trim();
      if (transcript) {
        opts.onMessage?.({
          id: `msg-${++messageIdRef.current}`,
          role: "user",
          content: transcript,
          timestamp: new Date().toISOString(),
          itemId: msg.item_id,
        });
      }
    }

    // Log all function calls and important events for debugging
    if (type === "response.function_call_arguments.done") {
      console.log("[realtime] Tool call:", msg.name, "call_id:", msg.call_id);
    }
    if (type === "input_audio_buffer.speech_started") {
      console.log("[realtime] VAD: speech detected");
      opts.onUserSpeechStarted?.();
    }
    if (type === "input_audio_buffer.speech_stopped") {
      console.log("[realtime] VAD: speech ended");
    }

    // Tool calls — handle hangUp / disconnectFromSession
    if (type === "response.function_call_arguments.done") {
      // Mode drill : l'outil hangUp n'est pas enregistré, mais garde défensive au cas où
      // le modèle tenterait quand même de raccrocher → on ignore (pas de fin prématurée).
      if (opts.disableHangUp && (msg.name === "hangUp" || msg.name === "disconnectFromSession" || msg.name === "disconnect")) {
        console.warn("[realtime] hangUp ignoré (mode drill : l'IA ne raccroche pas)");
        return;
      }
      if (msg.name === "hangUp" || msg.name === "disconnectFromSession" || msg.name === "disconnect") {
        console.log("[realtime] Agent called hangUp tool:", msg.name);
        // DIAGNOSTIC : fin déclenchée par le simulateur (tool). On capture MAINTENANT
        // (pc encore connecté → type de candidat ICE valide) ; le disconnect() qui suit
        // ne re-capturera pas (garde endDiagSentRef).
        endReasonRef.current = "agent_hangup_tool";
        void captureEndDiagnostic("agent_hangup_tool");
        // Send tool output before disconnecting
        if (dcRef.current?.readyState === "open") {
          dcRef.current.send(JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: msg.call_id,
              output: JSON.stringify({ result: "disconnected" }),
            },
          }));
        }
        // Wait for agent to finish speaking + audio buffer, then disconnect
        waitForAgentSilenceThen(() => disconnect(), 15000, 2500);
      }
    }

    // Error events
    if (type === "error") {
      const errorMsg = msg.error?.message || "Erreur OpenAI Realtime";
      console.error("[realtime] Server error:", msg.error);
      lastServerErrorRef.current = errorMsg; // DIAGNOSTIC : contexte pour la fin éventuelle
      setState((prev) => ({ ...prev, error: errorMsg }));
      opts.onError?.(new Error(errorMsg));
    }
  }, [disconnect, scheduleLiveFlush, captureEndDiagnostic]);

  const connect = useCallback(async (agentConfig: AgentConfig) => {
    // Garde anti-double-connexion (bug « coach en double » + micro non entendu) :
    // si une session existe déjà OU est en cours de création, on ignore l'appel.
    if (pcRef.current || connectingRef.current) {
      console.warn("[realtime] connect() ignoré : session déjà active ou en cours de connexion");
      return;
    }
    connectingRef.current = true;
    try {
      setState((prev) => ({ ...prev, error: null, micIssue: null }));

      // DIAGNOSTIC : reset des refs pour cette nouvelle session.
      connectStartRef.current = Date.now();
      endDiagSentRef.current = false;
      endReasonRef.current = null;
      iceDisconnectLoggedRef.current = false;
      lastServerErrorRef.current = null;

      // 1. Get microphone access with echo cancellation
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (micErr) {
        // getUserMedia a levé une exception : permission refusée OU aucun micro.
        const name = (micErr as DOMException)?.name || "";
        const issue: MicIssue =
          /NotFound|DevicesNotFound|OverconstrainedError/i.test(name) ? "no-device" : "denied";
        setState((prev) => ({ ...prev, micIssue: issue }));
        throw new Error(`getUserMedia ${issue}: ${name || String(micErr)}`);
      }
      streamRef.current = stream;

      // 1b. Détection du micro "silencieux" : getUserMedia a réussi mais la piste
      // ne fournit aucun son (`track.muted === true`) ou s'est terminée. Cas
      // typique après une mise à jour macOS qui a révoqué l'autorisation micro
      // de Chrome au niveau OS — l'app ne reçoit AUCUNE exception, l'utilisateur
      // parle dans le vide sans feedback. On lève un avertissement non bloquant
      // (≠ `error`) que l'UI affiche en encart d'aide.
      // NB : `track.muted` (lecture seule, piloté par l'OS) est distinct de
      // `track.enabled` (piloté par l'app pour le mute auto/manuel).
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack || audioTrack.readyState !== "live") {
        console.warn(`[realtime] Aucune piste micro exploitable (track=${!!audioTrack}, readyState=${audioTrack?.readyState})`);
        setState((prev) => ({ ...prev, micIssue: "no-device" }));
      } else {
        if (audioTrack.muted) {
          console.warn("[realtime] Piste micro en silence (track.muted=true) — autorisation OS probablement révoquée (ex. MAJ macOS)");
          setState((prev) => ({ ...prev, micIssue: "silent" }));
        }
        // Suivi en cours de session : l'OS peut couper/rétablir le flux à chaud.
        audioTrack.onmute = () => {
          console.warn("[realtime] Piste micro passée en silence pendant la session");
          setState((prev) => ({ ...prev, micIssue: "silent" }));
        };
        audioTrack.onunmute = () => {
          setState((prev) => (prev.micIssue === "silent" ? { ...prev, micIssue: null } : prev));
        };
        audioTrack.onended = () => {
          setState((prev) => ({ ...prev, micIssue: "no-device" }));
        };
      }

      // 2. Create RTCPeerConnection
      // Config ICE = 2 STUN Google, chemin DIRECT uniquement.
      // ⚠️ Repli TURN ABANDONNÉ (27/07/2026, prouvé par les diagnostics) : relayer le média temps
      // réel d'OpenAI Realtime via un relais TURN tiers (Cloudflare) est INSTABLE — le relais est
      // nominé À LA PLACE du direct (qui fonctionnerait) puis meurt en ~40-52 s
      // (ice_disconnected→failed), quel que soit le transport (UDP comme TLS). Un relais dans la
      // liste ICE est donc un PIÈGE, pas un filet. Le STUN obtient le candidat direct (srflx), le
      // seul chemin fiable vers OpenAI (answerer ICE-lite). Le STUN reste obligatoire depuis la GA
      // (22/05/2026) — sans lui : `WebRTC: ICE failed, add a STUN server`.
      // Support des réseaux firewall-stricts (qui exigeraient un relais) → fiche R&D séparée :
      // spec/rnd-firewall-turn-openai-realtime.md (piste : envoyer l'offre SDP APRÈS la fin du
      // gathering pour que le relais soit connu d'OpenAI). NE PAS ré-injecter de TURN ici sans elle.
      const iceServers: RTCIceServer[] = [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ];
      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      // Session établie : à partir d'ici la garde `pcRef.current` couvre les appels concurrents.
      connectingRef.current = false;

      // 3. Add microphone track
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // 4. Handle remote audio (agent's voice)
      pc.ontrack = (event) => {
        const remoteStream = event.streams[0];
        if (!remoteStream) return;
        const audio = new Audio();
        audio.srcObject = remoteStream;
        audio.autoplay = true;
        audioRef.current = audio;
        // Expose le flux à l'UI (visualiseur waveform). NB : l'élément <audio>
        // ci-dessus DOIT exister pour que Chrome alimente un AnalyserNode branché
        // sur ce même MediaStream (quirk connu des flux WebRTC distants).
        optionsRef.current.onAgentAudioStream?.(remoteStream);
      };

      // 5. Create data channel for OpenAI events
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = () => {
        console.log("[realtime] Data channel open");
        setState((prev) => ({ ...prev, isConnected: true }));
        optionsRef.current.onConnected?.();

        // Configure VAD + register hangUp tool + INJECTER LES INSTRUCTIONS PERSONA
        //
        // Fix critique 2026-05-15 (Gap 1 de l'audit IA) — Auparavant, `agentConfig.instructions`
        // (retourné par N8N pour chaque simulator + chaque type d'agent) n'était JAMAIS
        // transmis à OpenAI Realtime API. Le persona vocal tournait donc sans son brief
        // comportemental → réponses génériques, incohérentes avec le scénario, frustration
        // utilisateur récurrente. On les injecte désormais dans le 1er `session.update`.
        //
        // Migration GA (22/05/2026) :
        //   - session.type = "realtime" est désormais REQUIS
        //   - voice est dans audio.output.voice (plus au top-level)
        //   - turn_detection est dans audio.input.turn_detection (plus au top-level)
        //   - response.modalities a été supprimé (la session gère output_modalities)
        //
        // Impact : bénéficie à TOUS les simulateurs (Prospector, Discovery, Pitch,
        // Negotiator, Satisfactor, MEDDIC) — pas seulement Sales Coach Réel.
        // GA (22/05/2026) : la transcription de l'input user n'est PLUS activée
        // par défaut. Sans `audio.input.transcription`, l'event
        // `conversation.item.input_audio_transcription.completed` n'est jamais
        // émis → la transcription envoyée à N8N feedback contient uniquement
        // les répliques de l'agent (monologue) → N8N retourne `non_applicable`
        // et tous les scores tombent à 0. Whisper-1 reste le modèle par défaut
        // recommandé pour la transcription temps réel multilingue.
        const audioConfig: Record<string, unknown> = {
          input: {
            turn_detection: {
              type: "server_vad",
              threshold: 0.9,
              prefix_padding_ms: 300,
              silence_duration_ms: 1000,
              create_response: true,
              // OpenAI annule la réponse en cours quand l'user prend la parole
              // → évite l'erreur "Conversation already has an active response
              // in progress" (race entre le VAD auto-trigger et la réponse en
              // cours) + comportement plus humain (l'agent s'arrête pour écouter).
              interrupt_response: true,
            },
            transcription: { model: "whisper-1" },
          },
        };
        const sessionUpdate: Record<string, unknown> = {
          type: "realtime",
          // Mode drill (coach « S'exercer ») : on N'ENREGISTRE PAS l'outil hangUp →
          // l'IA ne peut pas raccrocher d'elle-même (elle coupait avant que l'apprenant
          // réponde). La session se termine uniquement sur action utilisateur.
          tools: optionsRef.current.disableHangUp ? [] : [
            {
              type: "function",
              name: "hangUp",
              // Déclencheur STRICT basé sur un FAIT OBSERVABLE (l'interlocuteur prend congé),
              // pas sur le jugement du modèle. Corrige le raccrochage prématuré : le simulateur
              // coupait dès qu'un RDV était proposé / l'objectif semblait atteint (81 % des fins
              // d'appel YNEIA du 10/07). Faux déclencheurs nommés (dont l'auto-congé) ; défaut = ne pas raccrocher.
              // Envoyée à OpenAI dans le session.update de CHAQUE session : une consigne
              // française arrivait aussi aux utilisateurs anglophones, au risque de faire
              // dériver la langue de l'agent vocal. Elle suit maintenant la langue de l'app.
              description: HANG_UP_DESCRIPTION[getCurrentLanguage().toLowerCase().startsWith("en") ? "en" : "fr"],
              parameters: { type: "object", properties: {} },
            },
          ],
          audio: audioConfig,
        };
        // Si N8N retourne un brief système (persona ou coach), on l'applique.
        if (agentConfig.instructions && agentConfig.instructions.trim().length > 0) {
          sessionUpdate.instructions = agentConfig.instructions;
          console.log(`[realtime] Brief persona injecté (${agentConfig.instructions.length} chars)`);
        } else {
          console.warn("[realtime] Aucune instruction persona reçue de N8N — le persona vocal sera générique");
        }
        // Si une voix est fournie par N8N, on la place dans audio.output.voice (GA)
        if (agentConfig.voice && agentConfig.voice.trim().length > 0) {
          audioConfig.output = { voice: agentConfig.voice };
        }
        dc.send(JSON.stringify({
          type: "session.update",
          session: sessionUpdate,
        }));

        // Mute mic initially, then trigger the agent greeting
        stream.getAudioTracks().forEach((t) => { t.enabled = false; });

        // Trigger the agent's opening line (e.g. "Oui ?")
        // GA : response.modalities supprimé → remplacé par output_modalities.
        // Sans cette précision le serveur peut générer une réponse texte sans
        // audio (l'utilisateur n'entend rien quand le simulateur décroche et
        // doit dire "allo ?" pour déclencher une vraie réponse vocale).
        dc.send(JSON.stringify({
          type: "response.create",
          response: { output_modalities: ["audio"] },
        }));
      };

      dc.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleServerEvent(msg);
        } catch {
          // ignore non-JSON
        }
      };

      dc.onclose = () => {
        console.log("[realtime] Data channel closed");
        // DIAGNOSTIC : fermeture du canal (réseau/serveur) = fin. Capture avant teardown ;
        // si un hangUp/disconnect a déjà capté, la garde endDiagSentRef évite le doublon.
        void captureEndDiagnostic(endReasonRef.current ?? "dc_close");
        // Only cleanup network, let audio buffer finish playing
        cleanupNetwork();
        optionsRef.current.onDisconnected?.();
      };

      dc.onerror = (event) => {
        console.error("[realtime] Data channel error:", event);
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "failed" || pc.iceConnectionState === "disconnected") {
          console.warn("[realtime] ICE connection state:", pc.iceConnectionState);
          // DIAGNOSTIC : ice_disconnected = indicateur avancé (souvent transitoire).
          // On le capte 1× (isEnd=false, ne termine pas) pour voir s'il précède un drop.
          if (pc.iceConnectionState === "disconnected" && !iceDisconnectLoggedRef.current) {
            iceDisconnectLoggedRef.current = true;
            void captureEndDiagnostic("ice_disconnected", false);
          }
          if (pc.iceConnectionState === "failed") {
            endReasonRef.current = "ice_failed";
            void captureEndDiagnostic("ice_failed"); // avant cleanupAll (pc encore lisible)
            cleanupAll();
            const err = new Error("Connexion WebRTC perdue");
            setState((prev) => ({ ...prev, error: err.message, isConnected: false }));
            optionsRef.current.onError?.(err);
          }
        }
      };

      // 6. Create SDP offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 7. Send offer to OpenAI Realtime API
      // Migration GA (mai 2026) : l'endpoint Beta /v1/realtime + le modèle
      // "gpt-4o-realtime-preview" ont été dépréciés. La GA utilise
      // /v1/realtime/calls + le modèle "gpt-realtime" (cf. erreur OpenAI
      // 22/05 "beta_api_shape_disabled" + header location de la réponse).
      const model = agentConfig.model || "gpt-realtime";
      // Reset cost accumulator pour cette session + mémorise le model
      usageAccRef.current = { inputTokens: 0, outputTokens: 0, cachedTokens: 0, audioInputTokens: 0, textInputTokens: 0, audioOutputTokens: 0, textOutputTokens: 0, model };
      console.log(`[realtime] Connecting to OpenAI Realtime, model=${model}`);

      const response = await fetch(`https://api.openai.com/v1/realtime/calls?model=${model}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${agentConfig.client_secret.value}`,
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI Realtime error (${response.status}): ${errorText}`);
      }

      // 7b. Capture du call_id (sideband) depuis l'en-tête `Location`.
      // OpenAI renvoie `Location: /v1/realtime/calls/rtc_…` sur la réponse de
      // POST /v1/realtime/calls. On en extrait le call_id et on le relaie à l'UI
      // (qui appellera POST /api/sideband). Best-effort, NON bloquant : un header
      // absent → `console.warn` + pas de call_id, la session vocale continue.
      // ⚠️ À VALIDER EN E2E RÉEL : le harnais du contrôleur a prouvé la lecture de
      // `Location` SANS `?model=` ; ici l'appel garde `?model=` (cf. flux existant).
      // Le query param ne devrait pas affecter l'exposition CORS de l'en-tête,
      // mais à confirmer sur une vraie session OpenAI.
      const callId = extractCallIdFromLocation(response.headers.get("Location"));
      if (callId) {
        optionsRef.current.onCallId?.(callId);
      } else {
        console.warn("[realtime] En-tête `Location` absent/illisible — pas de call_id sideband (non bloquant)");
      }

      // 8. Set remote SDP answer
      const answerSdp = await response.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      console.log("[realtime] WebRTC connected successfully");

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error("[realtime] Connection error:", err);
      connectingRef.current = false; // libère la garde (échec avant/pendant l'établissement)
      lastServerErrorRef.current = err.message;
      void captureEndDiagnostic("connect_error"); // échec d'établissement de la session
      cleanupAll();
      setState((prev) => ({ ...prev, error: err.message, isConnected: false }));
      optionsRef.current.onError?.(err);
      throw err;
    }
  }, [handleServerEvent, cleanupNetwork, cleanupAll, captureEndDiagnostic]);

  // Permet à l'UI de masquer l'encart d'aide micro (bouton "J'ai compris").
  const clearMicIssue = useCallback(() => {
    setState((prev) => (prev.micIssue ? { ...prev, micIssue: null } : prev));
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    userMutedRef.current = muted;
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !muted;
      });
    }
  }, []);

  const sendTextMessage = useCallback((text: string) => {
    if (!dcRef.current || dcRef.current.readyState !== "open") {
      console.error("[realtime] Data channel not open");
      return;
    }
    // Send text as a conversation item
    dcRef.current.send(JSON.stringify({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    }));
    // Trigger a response
    dcRef.current.send(JSON.stringify({
      type: "response.create",
    }));
  }, []);

  /**
   * Wait for the agent to finish speaking + audio buffer playback.
   * Returns a promise that resolves when it's safe to disconnect.
   * - Polls isAgentSpeakingRef every 200ms
   * - Once silent for bufferMs continuously, resolves
   * - Safety timeout at maxWaitMs
   */
  const waitForSilence = useCallback((maxWaitMs = 15000, bufferMs = 2500): Promise<void> => {
    return new Promise((resolve) => {
      waitForAgentSilenceThen(resolve, maxWaitMs, bufferMs);
    });
  }, []);

  /**
   * Snapshot des tokens consommés depuis le début de la session.
   * Lu par le caller en fin de session pour appeler trainingApi.logSessionCost.
   * Reset à chaque connect().
   */
  const getUsage = useCallback(() => ({
    inputTokens: usageAccRef.current.inputTokens,
    outputTokens: usageAccRef.current.outputTokens,
    cachedTokens: usageAccRef.current.cachedTokens,
    audioInputTokens: usageAccRef.current.audioInputTokens,
    textInputTokens: usageAccRef.current.textInputTokens,
    audioOutputTokens: usageAccRef.current.audioOutputTokens,
    textOutputTokens: usageAccRef.current.textOutputTokens,
    model: usageAccRef.current.model,
  }), []);

  // Permet à l'appelant (TrainingPage) de tagger la fin AVANT de déclencher endCall/disconnect
  // (ex. auto-fin 30s post-politesses, filet 90s) → le diagnostic enregistre le vrai motif
  // au lieu du défaut "user_hangup". Sans effet sur le comportement de la simu.
  const setEndReason = useCallback((reason: RealtimeEndDiagnostic["reason"]) => {
    endReasonRef.current = reason;
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    setMuted,
    clearMicIssue,
    sendTextMessage,
    waitForSilence,
    getUsage,
    setEndReason,
  };
}
